import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, tap } from 'rxjs';
import { ApplicationType, Enterprise } from 'shared-models';
import { EnterpriseModuleItem, EnterpriseModulePreferencesService } from './enterprise-module-preferences.service';
import { EnterpriseService } from './enterprise.service';

export type EnterpriseFeatureKey = 'fileManagement' | 'applications' | 'closeMonth' | 'absences' | 'projects';

export interface EnterpriseFeatureConfig {
  enterpriseId: string | null;
  loaded: boolean;
  closeMonthsEnabled: boolean;
  closeDay: number | null;
  absencesEnabled: boolean;
  allowedAbsenceTypes: ApplicationType[];
  fileManagementEnabled: boolean;
  applicationsEnabled: boolean;
  blockedTimerecordsHomeUserIds: string[];
  projectsEnabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class EnterpriseFeatureConfigService {
  private readonly ABSENCES_MODULE_NAME = 'ABSENCES';
  private readonly FILE_MANAGEMENT_MODULE_NAME = 'FILE_MANAGEMENT';
  private readonly TABLET_MODULE_NAME = 'TABLET';
  private readonly PROJECTS_MODULE_NAME = 'PROJECTS';
  private readonly defaultAbsenceTypes: ApplicationType[] = [
    ApplicationType.VACATION,
    ApplicationType.PERSONAL_MATTERS,
    ApplicationType.MEDICAL_LEAVE,
    ApplicationType.PAID_LEAVE,
    ApplicationType.UNPAID_LEAVE,
    ApplicationType.PAID_PARENTAL_LEAVE,
    ApplicationType.UNPAID_PARENTAL_LEAVE,
  ];
  private readonly allowedAbsenceTypesSet = new Set(this.defaultAbsenceTypes);

  private readonly configSubject = new BehaviorSubject<EnterpriseFeatureConfig>({
    enterpriseId: null,
    loaded: false,
    closeMonthsEnabled: false,
    closeDay: null,
    absencesEnabled: true,
    allowedAbsenceTypes: [...this.defaultAbsenceTypes],
    fileManagementEnabled: true,
    applicationsEnabled: true,
    blockedTimerecordsHomeUserIds: [],
    projectsEnabled: false,
  });

  readonly config$ = this.configSubject.asObservable();

  constructor(
    private readonly enterpriseService: EnterpriseService,
    private readonly enterpriseModulePreferencesService: EnterpriseModulePreferencesService
  ) {}

  getSnapshot(): EnterpriseFeatureConfig {
    return this.configSubject.value;
  }

  ensureLoaded(): Observable<EnterpriseFeatureConfig> {
    const currentEnterprise = this.enterpriseService.getSelectedEnterprise();
    const currentEnterpriseId = currentEnterprise?.id ?? null;
    const snapshot = this.getSnapshot();

    if (snapshot.loaded && snapshot.enterpriseId === currentEnterpriseId) {
      return of(snapshot);
    }

    return this.loadForCurrentEnterprise();
  }

  loadForCurrentEnterprise(): Observable<EnterpriseFeatureConfig> {
    const enterprise = this.enterpriseService.getSelectedEnterprise();
    if (!enterprise?.id) {
      const nextConfig: EnterpriseFeatureConfig = {
        enterpriseId: null,
        loaded: true,
        closeMonthsEnabled: false,
        closeDay: null,
        absencesEnabled: true,
        allowedAbsenceTypes: [...this.defaultAbsenceTypes],
        fileManagementEnabled: true,
        applicationsEnabled: true,
        blockedTimerecordsHomeUserIds: [],
        projectsEnabled: false,
      };
      this.configSubject.next(nextConfig);
      return of(nextConfig);
    }

    const closeData = this.resolveCloseMonthData(enterprise);

    return this.enterpriseModulePreferencesService.getEnterpriseModules(enterprise.id).pipe(
      catchError((error) => {
        console.error('[EnterpriseFeatureConfigService] Error loading enterprise modules', error);
        return of([] as EnterpriseModuleItem[]);
      }),
      map((modules) => {
        const absencesRelation = this.getModuleRelation(modules, this.ABSENCES_MODULE_NAME);
        const filesRelation = this.getModuleRelation(modules, this.FILE_MANAGEMENT_MODULE_NAME);
        const tabletRelation = this.getModuleRelation(modules, this.TABLET_MODULE_NAME);
        const projectsRelation = this.getModuleRelation(modules, this.PROJECTS_MODULE_NAME);

        const absencesEnabled = absencesRelation ? !!absencesRelation.active : true;
        const configuredAbsenceTypes = this.resolveAbsenceTypes(absencesRelation);
        const fileManagementEnabled = filesRelation ? !!filesRelation.active : true;
        const absencesVisible = absencesEnabled && configuredAbsenceTypes.length > 0;
        const blockedTimerecordsHomeUserIds = this.resolveBlockedTimerecordsHomeUserIds(tabletRelation);
        const projectsEnabled = projectsRelation ? !!projectsRelation.active : false;

        const nextConfig: EnterpriseFeatureConfig = {
          enterpriseId: enterprise.id,
          loaded: true,
          closeMonthsEnabled: closeData.enabled,
          closeDay: closeData.day,
          absencesEnabled,
          allowedAbsenceTypes: configuredAbsenceTypes,
          fileManagementEnabled,
          applicationsEnabled: closeData.enabled || absencesVisible,
          blockedTimerecordsHomeUserIds,
          projectsEnabled,
        };

        return nextConfig;
      }),
      tap((config) => this.configSubject.next(config))
    );
  }

  isFeatureEnabled(feature: EnterpriseFeatureKey): boolean {
    const snapshot = this.getSnapshot();
    switch (feature) {
      case 'fileManagement':
        return snapshot.fileManagementEnabled;
      case 'applications':
        return snapshot.applicationsEnabled;
      case 'closeMonth':
        return snapshot.closeMonthsEnabled;
      case 'absences':
        return snapshot.absencesEnabled;
      case 'projects':
        return snapshot.projectsEnabled;
      default:
        return false;
    }
  }

  private resolveCloseMonthData(enterprise: Enterprise): { enabled: boolean; day: number | null } {
    const closesMonths = Number(enterprise.closesMonths);
    if (Number.isFinite(closesMonths) && closesMonths > 0) {
      return { enabled: true, day: closesMonths };
    }

    return { enabled: false, day: null };
  }

  private getModuleRelation(modules: EnterpriseModuleItem[], moduleName: string): EnterpriseModuleItem | undefined {
    return modules.find((module) => module.moduleName === moduleName);
  }

  private resolveAbsenceTypes(absencesRelation?: EnterpriseModuleItem): ApplicationType[] {
    if (!absencesRelation) {
      return [...this.defaultAbsenceTypes];
    }

    const hasConfiguredAbsenceTypes = Object.prototype.hasOwnProperty.call(
      absencesRelation.configurations ?? {},
      'absenceTypes'
    );

    if (!hasConfiguredAbsenceTypes) {
      return [...this.defaultAbsenceTypes];
    }

    const rawTypes = (absencesRelation.configurations?.['absenceTypes'] as unknown[]) ?? [];
    const sanitized = rawTypes.filter(
      (value): value is ApplicationType =>
        typeof value === 'string' && this.allowedAbsenceTypesSet.has(value as ApplicationType)
    );

    return sanitized;
  }

  private resolveBlockedTimerecordsHomeUserIds(tabletRelation?: EnterpriseModuleItem): string[] {
    if (!tabletRelation?.configurations) {
      return [];
    }

    const raw = tabletRelation.configurations['blockedTimerecordsHome'];
    if (Array.isArray(raw)) {
      return raw.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    }

    if (raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>).userIds)) {
      return ((raw as Record<string, unknown>).userIds as unknown[])
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        .map((value) => value.trim());
    }

    return [];
  }
}
